import { useState, useCallback } from 'react';
import { 
  PromptContext, 
  GeneratedTemplate, 
  generateEnhancedPrompt, 
  validateGeneratedTemplate 
} from './promptTemplates';
import { audioToBase64 } from '../voice/audioUtils';

export interface AIGenerationState {
  isTranscribing: boolean;
  isGenerating: boolean;
  transcriptionResult: string | null;
  generatedTemplate: GeneratedTemplate | null;
  error: string | null;
  progress: 'idle' | 'transcribing' | 'generating' | 'complete' | 'error';
}

export interface AIGenerationHook {
  state: AIGenerationState;
  generateFromVoice: (audioBlob: Blob, context?: Partial<PromptContext>) => Promise<GeneratedTemplate>;
  generateFromText: (text: string, context?: Partial<PromptContext>) => Promise<GeneratedTemplate>;
  reset: () => void;
}

const INITIAL_STATE: AIGenerationState = {
  isTranscribing: false,
  isGenerating: false,
  transcriptionResult: null,
  generatedTemplate: null,
  error: null,
  progress: 'idle'
};

export const useAIGeneration = (): AIGenerationHook => {
  const [state, setState] = useState<AIGenerationState>(INITIAL_STATE);

  // Transcribe audio to text
  const transcribeAudio = useCallback(async (audioBlob: Blob): Promise<string> => {
    try {
      setState(prev => ({ 
        ...prev, 
        isTranscribing: true, 
        progress: 'transcribing',
        error: null 
      }));

      // Convert audio to base64
      const audioBase64 = await audioToBase64(audioBlob);

      // Call transcription API
      const response = await fetch('/api/neural-templates/transcribe', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          audio: audioBase64,
          format: audioBlob.type || 'audio/webm'
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Transcription failed' }));
        throw new Error(errorData.error || `Transcription failed (Status: ${response.status})`);
      }

      const data = await response.json();
      const transcription = data.transcription || data.text || '';

      if (!transcription.trim()) {
        throw new Error('No speech detected in the recording');
      }

      setState(prev => ({ 
        ...prev, 
        transcriptionResult: transcription,
        isTranscribing: false 
      }));

      return transcription;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Transcription failed';
      setState(prev => ({ 
        ...prev, 
        isTranscribing: false,
        error: errorMessage,
        progress: 'error'
      }));
      throw error;
    }
  }, []);

  // Generate template from text
  const generateTemplate = useCallback(async (text: string, context: Partial<PromptContext> = {}): Promise<GeneratedTemplate> => {
    try {
      setState(prev => ({ 
        ...prev, 
        isGenerating: true,
        progress: 'generating',
        error: null 
      }));

      // Build full context
      const fullContext: PromptContext = {
        userInput: text,
        industry: 'general',
        templateType: 'consultation',
        difficulty: 'beginner',
        estimatedDuration: '15-30 minutes',
        ...context
      };

      // Generate enhanced prompt
      const prompt = generateEnhancedPrompt(fullContext);

      // Call generation API
      const response = await fetch('/api/neural-templates/generate', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt,
          context: fullContext,
          voicePrompt: text // For backwards compatibility
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Generation failed' }));
        throw new Error(errorData.error || `Generation failed (Status: ${response.status})`);
      }

      const data = await response.json();
      const template = data.template;

      // Validate generated template
      if (!validateGeneratedTemplate(template)) {
        throw new Error('Generated template has invalid structure');
      }

      setState(prev => ({ 
        ...prev, 
        generatedTemplate: template,
        isGenerating: false,
        progress: 'complete'
      }));

      return template;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Template generation failed';
      setState(prev => ({ 
        ...prev, 
        isGenerating: false,
        error: errorMessage,
        progress: 'error'
      }));
      throw error;
    }
  }, []);

  // Generate from voice (transcribe + generate)
  const generateFromVoice = useCallback(async (audioBlob: Blob, context?: Partial<PromptContext>): Promise<GeneratedTemplate> => {
    try {
      // Step 1: Transcribe audio
      const transcription = await transcribeAudio(audioBlob);
      
      // Step 2: Generate template
      const template = await generateTemplate(transcription, context);
      
      return template;

    } catch (error) {
      throw error; // Error state already set by individual functions
    }
  }, [transcribeAudio, generateTemplate]);

  // Generate from text directly
  const generateFromText = useCallback(async (text: string, context?: Partial<PromptContext>): Promise<GeneratedTemplate> => {
    if (!text.trim()) {
      const error = 'Please provide text input for template generation';
      setState(prev => ({ ...prev, error, progress: 'error' }));
      throw new Error(error);
    }

    return generateTemplate(text, context);
  }, [generateTemplate]);

  // Reset state
  const reset = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  return {
    state,
    generateFromVoice,
    generateFromText,
    reset
  };
}; 